// 📄 catalogoTransaccionesService.ts
// Catálogo global de Transacciones SAP (desarrollo/ABAP, monitoreo, MM/
// compras, configuración, transportes y seguridad) de uso diario en las
// integraciones SAP Ariba / S/4HANA. Mismo criterio que
// catalogoBadisService.ts / catalogoApisService.ts: CRUD completo solo
// desde el panel Admin, lectura abierta a cualquier consultor autenticado
// (ver firestore.rules, colección `catalogoTransacciones`).
import {
  doc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';

export type SolucionTransaccion = 'general' | 'commerce' | 'buying' | 'sourcing' | 'slp' | 'contracts' | 'masterdata';

export interface SolucionTransaccionMeta {
  id: SolucionTransaccion;
  label: string;
}

export const SOLUCIONES_TRANSACCION: SolucionTransaccionMeta[] = [
  { id: 'general', label: 'General' },
  { id: 'commerce', label: 'Commerce Automation' },
  { id: 'buying', label: 'Buying' },
  { id: 'sourcing', label: 'Sourcing' },
  { id: 'slp', label: 'SLP' },
  { id: 'contracts', label: 'Contracts' },
  { id: 'masterdata', label: 'Master Data' }
];

export type CategoriaTransaccion = 'abap' | 'monitoreo' | 'compras' | 'configuracion' | 'transportes' | 'seguridad';

export interface CategoriaTransaccionMeta {
  id: CategoriaTransaccion;
  label: string;
}

export const CATEGORIAS_TRANSACCION: CategoriaTransaccionMeta[] = [
  { id: 'abap', label: 'Desarrollo y Objetos ABAP' },
  { id: 'monitoreo', label: 'Monitoreo, Logs y Mensajería' },
  { id: 'compras', label: 'MM / Compras, Recepciones y Facturas' },
  { id: 'configuracion', label: 'Configuración, Jobs y Sistema' },
  { id: 'transportes', label: 'Transportes' },
  { id: 'seguridad', label: 'Seguridad y Autorizaciones' }
];

export const categoriaTransaccionMeta = (categoria: CategoriaTransaccion): CategoriaTransaccionMeta =>
  CATEGORIAS_TRANSACCION.find((c) => c.id === categoria) || CATEGORIAS_TRANSACCION[0];

export const solucionTransaccionMeta = (solucion: SolucionTransaccion): SolucionTransaccionMeta =>
  SOLUCIONES_TRANSACCION.find((s) => s.id === solucion) || SOLUCIONES_TRANSACCION[0];

// Objeto de negocio: taxonomía compartida usada hoy por el Catálogo de
// Interfaces/Canales (ver catalogoInterfacesService.ts) para resolver el
// caso de uso real — el usuario sabe QUÉ documento le llegó mal ("un
// contrato", "una factura"), no en qué transacción buscarlo. Vive acá,
// junto a SOLUCIONES_TRANSACCION, por si en el futuro otro catálogo global
// necesita la misma clasificación.
export type ObjetoNegocio =
  | 'contratos' | 'ordenesCompra' | 'confirmacionesPedido' | 'avisosEntrega'
  | 'entradaMercancias' | 'hojaEntradaServicios' | 'facturas' | 'proveedores'
  | 'solicitudesPedido' | 'datosMaestros' | 'catalogosCif' | 'otro';

export interface ObjetoNegocioMeta {
  id: ObjetoNegocio;
  label: string;
}

export const OBJETOS_NEGOCIO: ObjetoNegocioMeta[] = [
  { id: 'contratos', label: 'Contratos' },
  { id: 'ordenesCompra', label: 'Órdenes de compra' },
  { id: 'confirmacionesPedido', label: 'Confirmaciones de pedido' },
  { id: 'avisosEntrega', label: 'Avisos de entrega/ASN' },
  { id: 'entradaMercancias', label: 'Entrada de mercancías (MIGO)' },
  { id: 'hojaEntradaServicios', label: 'Hoja de entrada de servicios (HES)' },
  { id: 'facturas', label: 'Facturas' },
  { id: 'proveedores', label: 'Proveedores' },
  { id: 'solicitudesPedido', label: 'Solicitudes de pedido' },
  { id: 'datosMaestros', label: 'Datos maestros' },
  { id: 'catalogosCif', label: 'Catálogos (CIF)' },
  { id: 'otro', label: 'Otro' }
];

export const objetoNegocioMeta = (objetoNegocio: string): ObjetoNegocioMeta | null =>
  OBJETOS_NEGOCIO.find((o) => o.id === objetoNegocio) || null;

export interface TransaccionSapData {
  transaccion: string;
  nombre: string;
  categoria: CategoriaTransaccion;
  solucion: SolucionTransaccion;
  descripcion: string;
}

export interface TransaccionSap extends TransaccionSapData {
  id: string;
  createdAt?: any;
  updatedAt?: any;
}

const CATALOGO_COLLECTION = "catalogoTransacciones";

export const getTransacciones = async (): Promise<TransaccionSap[]> => {
  const snap = await getDocs(collection(db, CATALOGO_COLLECTION));
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      transaccion: data.transaccion || '',
      nombre: data.nombre || '',
      categoria: (data.categoria as CategoriaTransaccion) || 'abap',
      solucion: (data.solucion as SolucionTransaccion) || 'general',
      descripcion: data.descripcion || '',
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  });
};

/**
 * Crea (transaccionId = null) o actualiza (transaccionId = id existente) un
 * registro. Igual que catalogoBadisService.ts, el id lo genera el llamador
 * cuando es nuevo (ver generarId en TransactionFormModal.tsx).
 */
export const saveTransaccion = async (transaccionId: string, data: TransaccionSapData, esNuevo: boolean): Promise<void> => {
  if (!data.transaccion.trim()) throw new Error("El código de transacción es obligatorio.");
  if (!data.nombre.trim()) throw new Error("El nombre/propósito de la transacción es obligatorio.");

  const payload: Record<string, any> = {
    transaccion: data.transaccion.trim().toUpperCase(),
    nombre: data.nombre.trim(),
    categoria: data.categoria,
    solucion: data.solucion,
    descripcion: data.descripcion.trim(),
    updatedAt: serverTimestamp()
  };
  if (esNuevo) payload.createdAt = serverTimestamp();

  await setDoc(doc(db, CATALOGO_COLLECTION, transaccionId), payload, { merge: true });
};

export const deleteTransaccion = async (transaccionId: string): Promise<void> => {
  await deleteDoc(doc(db, CATALOGO_COLLECTION, transaccionId));
};

// ── Sembrado inicial ──────────────────────────────────────────────────
// Ids determinísticos (a partir del código de transacción) para que
// sembrar sea idempotente: correrlo más de una vez pisa los mismos
// registros en vez de duplicarlos.
const slugificarId = (transaccion: string) =>
  transaccion.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const SEED_TRANSACCIONES: TransaccionSapData[] = [
  // Desarrollo y Objetos ABAP
  { transaccion: 'SE18', nombre: 'Definición y ampliación de BAdIs / Enhancements', categoria: 'abap', solucion: 'general', descripcion: 'Definición y ampliación de BAdIs / Enhancements.' },
  { transaccion: 'SE38', nombre: 'Editor de programas ABAP', categoria: 'abap', solucion: 'general', descripcion: 'Editor de programas ABAP.' },
  { transaccion: 'SE37', nombre: 'Módulos de funciones y BAPIs', categoria: 'abap', solucion: 'general', descripcion: 'Módulos de funciones y BAPIs.' },
  { transaccion: 'SE11', nombre: 'Diccionario de datos ABAP (Tablas y Estructuras)', categoria: 'abap', solucion: 'general', descripcion: 'Diccionario de datos ABAP (Tablas y Estructuras).' },
  { transaccion: 'SE24', nombre: 'Class Builder (Clases ABAP)', categoria: 'abap', solucion: 'general', descripcion: 'Class Builder (Clases ABAP).' },
  { transaccion: 'SE16N', nombre: 'Visualizador de tablas de base de datos', categoria: 'abap', solucion: 'general', descripcion: 'Visualizador de tablas de base de datos.' },
  { transaccion: 'SE91', nombre: 'Mantenimiento de mensajes de error', categoria: 'abap', solucion: 'general', descripcion: 'Mantenimiento de mensajes de error.' },
  { transaccion: 'CMOD', nombre: 'Gestión de proyectos de ampliaciones (Enhancements)', categoria: 'abap', solucion: 'general', descripcion: 'Gestión de proyectos de ampliaciones (Enhancements).' },
  { transaccion: 'SMOD', nombre: 'Validación de Enhancements estándar', categoria: 'abap', solucion: 'general', descripcion: 'Validación de Enhancements estándar.' },

  // Monitoreo, Logs y Mensajería
  { transaccion: 'SRT_MONI', nombre: 'Monitoreo de servicios Web / SOAP / OData', categoria: 'monitoreo', solucion: 'general', descripcion: 'Monitoreo de servicios Web / SOAP / OData.' },
  { transaccion: 'SRT_LOG', nombre: 'Registro de errores de Web Services', categoria: 'monitoreo', solucion: 'general', descripcion: 'Registro de errores de Web Services.' },
  { transaccion: 'SLG1', nombre: 'Registro de logs de aplicación del sistema', categoria: 'monitoreo', solucion: 'general', descripcion: 'Registro de logs de aplicación del sistema.' },
  { transaccion: 'WE02', nombre: 'Lista de IDOCs recibidos y enviados', categoria: 'monitoreo', solucion: 'general', descripcion: 'Lista de IDOCs recibidos y enviados.' },
  { transaccion: 'BD87', nombre: 'Reprocesamiento de IDOCs originales', categoria: 'monitoreo', solucion: 'general', descripcion: 'Reprocesamiento de IDOCs originales.' },
  { transaccion: 'WE19', nombre: 'Herramienta de prueba y copia de IDOCs', categoria: 'monitoreo', solucion: 'general', descripcion: 'Herramienta de prueba y copia de IDOCs.' },
  { transaccion: 'ST22', nombre: 'Análisis de errores en tiempo de ejecución (ABAP Dumps)', categoria: 'monitoreo', solucion: 'general', descripcion: 'Análisis de errores en tiempo de ejecución (ABAP Dumps).' },
  { transaccion: 'SXI_MONITOR', nombre: 'Monitoreo de integración SAP PI / PO', categoria: 'monitoreo', solucion: 'general', descripcion: 'Monitoreo de integración SAP PI / PO.' },
  { transaccion: 'DRFLOG', nombre: 'Logs de replicación de datos maestros (SLP / DRFF)', categoria: 'monitoreo', solucion: 'slp', descripcion: 'Logs de replicación de datos maestros (SLP / DRFF).' },
  { transaccion: 'SOST', nombre: 'Monitoreo de envíos de correo electrónico (Spool Email)', categoria: 'monitoreo', solucion: 'general', descripcion: 'Monitoreo de envíos de correo electrónico (Spool Email).' },
  { transaccion: 'SM21', nombre: 'Registro de diario del sistema SAP', categoria: 'monitoreo', solucion: 'general', descripcion: 'Registro de diario del sistema SAP.' },
  { transaccion: 'MB90', nombre: 'Reenvío de mensajes de impresiones/recepciones (MIGO)', categoria: 'monitoreo', solucion: 'commerce', descripcion: 'Reenvío de mensajes de impresiones/recepciones (MIGO).' },

  // MM / Compras, Recepciones y Facturas
  { transaccion: 'ME21N / ME23N', nombre: 'Crear / Visualizar Orden de Compra', categoria: 'compras', solucion: 'buying', descripcion: 'Crear / Visualizar Orden de Compra.' },
  { transaccion: 'ME53N', nombre: 'Visualizar Solicitud de Pedido / Requisición', categoria: 'compras', solucion: 'buying', descripcion: 'Visualizar Solicitud de Pedido / Requisición.' },
  { transaccion: 'ME29N', nombre: 'Liberación individual de Órdenes de Compra', categoria: 'compras', solucion: 'buying', descripcion: 'Liberación individual de Órdenes de Compra.' },
  { transaccion: 'ML81N', nombre: 'Hoja de Entrada de Servicios (HES)', categoria: 'compras', solucion: 'commerce', descripcion: 'Hoja de Entrada de Servicios (HES).' },
  { transaccion: 'MIGO', nombre: 'Entrada de mercancías y movimientos de stock', categoria: 'compras', solucion: 'commerce', descripcion: 'Entrada de mercancías y movimientos de stock.' },
  { transaccion: 'MB51', nombre: 'Historial de documentos de material (MIGO)', categoria: 'compras', solucion: 'commerce', descripcion: 'Historial de documentos de material (MIGO).' },
  { transaccion: 'MIRO / MIR4', nombre: 'Contabilización / Visualización de Facturas', categoria: 'compras', solucion: 'commerce', descripcion: 'Contabilización / Visualización de Facturas.' },
  { transaccion: 'MR8M', nombre: 'Cancelación de factura contabilizada', categoria: 'compras', solucion: 'commerce', descripcion: 'Cancelación de factura contabilizada.' },
  { transaccion: 'VL33N', nombre: 'Visualizar Aviso de Expedición (ASN)', categoria: 'compras', solucion: 'commerce', descripcion: 'Visualizar Aviso de Expedición (ASN).' },
  { transaccion: 'ME38 / ME33L', nombre: 'Plan de entregas / Schedule Agreements', categoria: 'compras', solucion: 'buying', descripcion: 'Plan de entregas / Schedule Agreements.' },

  // Configuración, Jobs y Sistema
  { transaccion: 'SM59', nombre: 'Configuración de conexiones RFC y Destinos', categoria: 'configuracion', solucion: 'general', descripcion: 'Configuración de conexiones RFC y Destinos.' },
  { transaccion: 'SOAMANAGER', nombre: 'Configuración web de servicios Web Services / BTP', categoria: 'configuracion', solucion: 'general', descripcion: 'Configuración web de servicios Web Services / BTP.' },
  { transaccion: 'AL11', nombre: 'Explorador de archivos e interfaces del servidor SAP', categoria: 'configuracion', solucion: 'general', descripcion: 'Explorador de archivos e interfaces del servidor SAP.' },
  { transaccion: 'SM36 / SM37', nombre: 'Creación y monitoreo de Jobs en fondo', categoria: 'configuracion', solucion: 'general', descripcion: 'Creación y monitoreo de Jobs en fondo.' },
  { transaccion: 'NACE', nombre: 'Determinación de mensajes y formularios', categoria: 'configuracion', solucion: 'general', descripcion: 'Determinación de mensajes y formularios.' },
  { transaccion: 'MMPV', nombre: 'Apertura de periodos contables MM', categoria: 'configuracion', solucion: 'commerce', descripcion: 'Apertura de periodos contables MM.' },
  { transaccion: 'SWEC / SWETYPV', nombre: 'Eventos y acopladores de workflow para HES', categoria: 'configuracion', solucion: 'commerce', descripcion: 'Eventos y acopladores de workflow para HES.' },

  // Transportes
  { transaccion: 'SE09 / SE10', nombre: 'Organizador de transportes (Órdenes de transporte)', categoria: 'transportes', solucion: 'general', descripcion: 'Organizador de transportes (Órdenes de transporte).' },
  { transaccion: 'STMS', nombre: 'Sistema de gestión de transportes entre ambientes', categoria: 'transportes', solucion: 'general', descripcion: 'Sistema de gestión de transportes entre ambientes.' },
  { transaccion: 'SE03', nombre: 'Herramientas de transporte / Desbloqueo de OTs', categoria: 'transportes', solucion: 'general', descripcion: 'Herramientas de transporte / Desbloqueo de OTs.' },

  // Seguridad y Autorizaciones
  { transaccion: 'SU53', nombre: 'Análisis de falta de autorizaciones/permisos', categoria: 'seguridad', solucion: 'general', descripcion: 'Análisis de falta de autorizaciones/permisos.' },
  { transaccion: 'SU01', nombre: 'Gestión de usuarios', categoria: 'seguridad', solucion: 'general', descripcion: 'Gestión de usuarios.' },
  { transaccion: 'SU24', nombre: 'Verificación de objetos de autorización por transacción', categoria: 'seguridad', solucion: 'general', descripcion: 'Verificación de objetos de autorización por transacción.' },
  { transaccion: 'AGR_1251', nombre: 'Tabla de campos y objetos de autorización', categoria: 'seguridad', solucion: 'general', descripcion: 'Tabla de campos y objetos de autorización.' }
];

/**
 * Carga las transacciones de ejemplo (ver SEED_TRANSACCIONES). Idempotente:
 * usa un id determinístico por código de transacción, así correrlo de
 * nuevo actualiza los mismos registros en vez de duplicarlos.
 */
export const seedCatalogoTransacciones = async (): Promise<void> => {
  await Promise.all(
    SEED_TRANSACCIONES.map((data) => saveTransaccion(slugificarId(data.transaccion), data, true))
  );
};
